import { Link } from 'react-router-dom';
import { useLanguage } from '../../i18n/LanguageContext';
import { siteContent } from '../../data/siteContent';
import { SectionHeading, SocialIcon, Surface } from '../../components/SitePrimitives';
import WhatsAppIcon from '../../components/WhatsAppIcon';

const ServiceCard = ({ service, learnMore }) => (
    <Link to={service.path} className="service-card group">
        <div className="flex items-start gap-5">
            <div className="service-icon-wrap">
                <img src={service.icon} alt="" className="h-10 w-10 object-contain" />
            </div>
            <div className="min-w-0 flex-1">
                <h3 className="text-xl font-medium tracking-tight text-brown-50">{service.title}</h3>
                <p className="mt-2 text-sm leading-7 text-stone-600">{service.short}</p>
                <span className="mt-4 inline-flex items-center gap-2 text-sm font-semibold text-brown-50 transition-transform duration-200 group-hover:translate-x-1">
                    {learnMore} <span aria-hidden="true">↗</span>
                </span>
            </div>
        </div>
    </Link>
);

const Home = () => {
    const { language } = useLanguage();
    const content = siteContent[language];
    const scrollToServices = (event) => {
        event.preventDefault();
        document.getElementById('services')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    };

    return (
        <main className="bg-nude-50">
            <section className="relative isolate overflow-hidden">
                <img src="/assets/updates/home-hero.png" alt="Neuropsychologist and clinical psychologist Sophia Boukoulo" className="h-[30rem] w-full object-cover object-[74%_center] sm:h-[40rem] sm:object-center" />
                <div className="absolute inset-0 bg-gradient-to-r from-[#2d211e]/95 via-[#2d211e]/65 to-[#2d211e]/20" />
                <div className="absolute inset-x-0 bottom-0 mx-auto max-w-7xl px-6 pb-10 sm:px-10 sm:pb-14">
                    <p className="section-eyebrow text-white/80">{content.home.eyebrow}</p>
                    <h1 className="mt-3 max-w-xl text-4xl font-medium leading-[0.98] tracking-[-0.06em] text-white sm:text-6xl">{content.home.heroTitle}</h1>
                    <p className="mt-5 max-w-lg text-base leading-7 text-white drop-shadow-[0_2px_10px_rgba(0,0,0,0.65)] sm:text-lg">{content.home.heroText}</p>
                    <a href="#services" onClick={scrollToServices} className="mt-7 inline-flex items-center gap-3 rounded-full bg-white px-5 py-3 text-sm font-semibold text-brown-50 transition-transform duration-200 hover:-translate-y-1">{content.home.discover} <span aria-hidden="true">↓</span></a>
                </div>
            </section>

            <div className="mx-auto max-w-7xl px-5 py-20 sm:px-8 sm:py-28">
                <section>
                    <SectionHeading eyebrow={content.home.eyebrows.resources} title={content.home.ebooksTitle} intro={content.home.ebooksIntro} />
                    <div className="grid gap-6 lg:grid-cols-2">
                        {content.home.ebooks.map((ebook) => (
                            <Surface key={ebook.link} className="flex flex-col gap-7 p-6 sm:flex-row sm:items-center sm:p-8">
                                <div className="flex min-h-44 w-full items-center justify-center rounded-2xl bg-[#f5e9e0] p-5 sm:w-40 sm:shrink-0">
                                    <img src={ebook.cover} alt="" className="max-h-40 w-32 object-contain" />
                                </div>
                                <div>
                                    <h3 className="text-2xl font-medium tracking-tight text-brown-50">{ebook.title}</h3>
                                    <p className="mt-3 text-sm leading-7 text-stone-600">{ebook.description}</p>
                                    <a href={ebook.link} target="_blank" rel="noopener noreferrer" className="button-primary mt-5">{content.home.ebookButton} <span aria-hidden="true">↗</span></a>
                                </div>
                            </Surface>
                        ))}
                    </div>
                </section>

                <div className="editorial-divider"><span>✦</span></div>

                <section>
                    <SectionHeading eyebrow={content.home.eyebrows.innovation} title={content.home.appTitle} />
                    <Surface className="grid items-center gap-8 p-7 sm:p-10 lg:grid-cols-[0.8fr_1.2fr]">
                        <div className="flex min-h-64 items-center justify-center overflow-hidden rounded-2xl bg-[#c56d4c] p-7">
                            <img src={content.app.image} alt="Petit Pas" className="max-h-56 w-full max-w-xs object-contain" />
                        </div>
                        <div>
                            <p className="text-2xl font-medium leading-tight tracking-tight text-brown-50">Petit Pas</p>
                            <p className="mt-4 text-base leading-8 text-stone-600">{content.home.appDescription}</p>
                            <p className="mt-5 font-medium italic text-brown-50">{content.home.appTagline}</p>
                            <div className="mt-7 flex flex-wrap gap-3">
                                <a href={content.app.appStore} target="_blank" rel="noopener noreferrer" className="button-secondary">{content.home.appStore} <span aria-hidden="true">↗</span></a>
                                <a href={content.app.playStore} target="_blank" rel="noopener noreferrer" className="button-secondary">{content.home.playStore} <span aria-hidden="true">↗</span></a>
                            </div>
                        </div>
                    </Surface>
                </section>

                <section id="services" className="scroll-mt-8 pt-28">
                    <SectionHeading eyebrow={content.home.eyebrows.support} title={content.home.servicesTitle} intro={content.home.servicesIntro} />
                    <div className="grid gap-5 lg:grid-cols-2">
                        {content.services.map((service) => <ServiceCard key={service.key} service={service} learnMore={content.home.learnMore} />)}
                    </div>
                </section>

                <section className="grid gap-6 pt-28 lg:grid-cols-[0.8fr_1.2fr]">
                    <div className="overflow-hidden rounded-[1.3rem]">
                        <img src="/assets/brief/about.jpeg" alt="Sophia Boukoulo in her practice" className="h-full min-h-80 w-full object-cover" />
                    </div>
                    <Surface className="p-7 sm:p-10">
                        <SectionHeading align="left" eyebrow={content.home.eyebrows.practice} title={content.home.practiceTitle} />
                        <div className="space-y-4 text-base leading-8 text-stone-600">
                            {content.home.practiceParagraphs.map((paragraph) => <p key={paragraph}>{paragraph}</p>)}
                        </div>
                    </Surface>
                </section>

                <section className="pt-28">
                    <SectionHeading eyebrow={content.home.eyebrows.contact} title={content.home.contactTitle} intro={content.home.contactText} />
                    <Surface className="flex flex-col items-center justify-between gap-7 p-7 sm:flex-row sm:p-10">
                        <a href={content.social.whatsapp} target="_blank" rel="noopener noreferrer" className="button-primary">{content.home.whatsapp} <span aria-hidden="true">↗</span></a>
                        <div className="flex items-center gap-3">
                            <SocialIcon href={content.social.linkedin} label="LinkedIn"><img src="/assets/icons/linkedinFill.png" alt="" className="h-7 w-7 object-contain" /></SocialIcon>
                            <SocialIcon href={content.social.instagram} label="Instagram"><img src="/assets/icons/instagramFill.png" alt="" className="h-7 w-7 object-contain" /></SocialIcon>
                            <SocialIcon href={content.social.whatsapp} label="WhatsApp"><WhatsAppIcon className="h-6 w-6" /></SocialIcon>
                        </div>
                    </Surface>
                </section>
            </div>
        </main>
    );
};

export default Home;
